import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useMemo } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { ClientResource } from '../../../../../../types/api/clients';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';
import { FormValues } from '../CreateEditDialog.functions';

type Props = {
  clientData: ClientResource | undefined;
  isEditDialog: boolean;
};

function ChargingDepartmentsTab({ clientData, isEditDialog }: Props) {
  const { t } = useTranslation();

  const {
    data: headquarterSubsidiaries,
    isLoading: isHeadquarterSubsidiariesLoading,
  } = useAxiosHook<ClientResource[]>(
    `/clients/${clientData?.direkcija_id}/subsidiaries`,
    {
      skipWhen: !clientData?.direkcija_id,
    }
  );

  const { append, remove, fields } = useFieldArray<FormValues, 'departments'>({
    name: 'departments',
  });

  const clientOptions = useMemo(
    () => [
      ...(!isEditDialog
        ? [
            {
              label: t('Registering customer'),
              value: 0,
            },
          ]
        : []),
      ...(headquarterSubsidiaries?.length
        ? headquarterSubsidiaries?.map((client) => ({
            label: client.ime,
            value: client.id,
          }))
        : []),
    ],
    [headquarterSubsidiaries, isEditDialog, t]
  );

  return (
    <div className="charging-departments-tab">
      <div>
        <Button
          type="button"
          icon="fas fa-plus"
          label={t('Add New')}
          className="p-mb-3"
          onClick={() =>
            append({
              id: -(fields!.length + 1),
              name: '',
              headquarters_id: clientData?.id ?? 0,
              charge_customer_id: 0,
            })
          }
        />
        <div className="p-fluid departments">
          {fields &&
            fields.length &&
            fields?.map((_, index) => (
              <div className="charging-department" key={index}>
                {fields?.length > 1 && (
                  <Button
                    type="button"
                    icon="fas fa-trash"
                    className="p-button-outlined delete-department-btn"
                    onClick={() => remove(index)}
                  />
                )}
                <div className="department-name">
                  <FieldWithErrors
                    name={`departments[${index}].name`}
                    label={t('Department')}
                  >
                    <Controller
                      name={`departments[${index}].name`}
                      render={({ field }) => (
                        <InputText
                          id={`departments[${index}].name`}
                          name={`departments[${index}].name`}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </FieldWithErrors>
                </div>

                <div className="department-client">
                  <FieldWithErrors
                    name={`departments[${index}].charge_customer_id`}
                    label={t('Client')}
                  >
                    <Controller
                      name={`departments[${index}].charge_customer_id`}
                      render={({ field }) => (
                        <Dropdown
                          id={`departments[${index}].charge_customer_id`}
                          options={clientOptions}
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                          placeholder={
                            isHeadquarterSubsidiariesLoading
                              ? t('Loading...')
                              : t('Select a client')
                          }
                        />
                      )}
                    />
                  </FieldWithErrors>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default ChargingDepartmentsTab;
